<script lang="ts">
  import { onDestroy, onMount } from "svelte"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import FormWrapper, { type FormContext } from "@kontextwerk/web-sdk/ui/layout/FormWrapper.svelte"
  import {
    removeAllErrors,
    validateField,
    validateInput,
  } from "@kontextwerk/web-sdk/core/forms/formValidation"
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import { mdiClose, mdiContentSave } from "@mdi/js"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import {
    createOrderType,
    updateOrderType,
  } from "../../../../../../../../api/kehrblatt/order-types"
  import {
    ceilPositiveMinutesToStep,
    SCHEDULING_MINUTE_STEP,
  } from "../../../../../../../../_shared/_logic/time-granularity"
  import type { Snippet } from "svelte"

  type OverlayHostProps = {
    onremoveOverlay?: () => void
    onshowOverlay?: () => void
    setActionButtonSnippet?: (snippet: Snippet | null) => void
  }

  type Props = {
    organizationId: string
    orderType?: OrderType | null
    onSaved?: ((orderType: OrderType) => void | Promise<void>) | null
  }

  type OrderTypeForm = {
    internal_label: string
    public_label: string
    description: string
    typical_duration_min: string
    is_standard_type: boolean
  }

  const fieldIds = {
    internalLabel: "order-type-internal-label",
    publicLabel: "order-type-public-label",
    description: "order-type-description",
    typicalDuration: "order-type-typical-duration",
    standardType: "order-type-standard-type",
  }

  const parseOptionalNumber = (value: string | number | null | undefined) => {
    if (value === null || value === undefined) return null

    const parsedInput = String(value).trim()

    if (!parsedInput) return null

    return ceilPositiveMinutesToStep(parsedInput, null)
  }

  let {
    organizationId,
    orderType = null,
    onSaved = null,
    onremoveOverlay = null,
    onshowOverlay = null,
    setActionButtonSnippet = null,
  }: Props & OverlayHostProps = $props()

  let submitting = $state(false)
  let submitForm = $state<(() => Promise<void>) | null>(null)

  const resetForm = (): OrderTypeForm => ({
    internal_label: orderType?.internal_label ?? "",
    public_label: orderType?.public_label ?? "",
    description: orderType?.description ?? "",
    typical_duration_min:
      orderType?.typical_duration_min !== null && orderType?.typical_duration_min !== undefined
        ? String(orderType.typical_duration_min)
        : "",
    is_standard_type: Boolean(orderType?.is_standard_type),
  })

  const close = (submitting: boolean) => {
    if (submitting) return

    onremoveOverlay?.()
  }

  onMount(() => {
    onshowOverlay?.()
    setActionButtonSnippet?.(actionButtons)
  })

  onDestroy(() => {
    setActionButtonSnippet?.(null)
  })

  const validateOrderType = ({
    data,
    form,
  }: {
    data: OrderTypeForm
    form: HTMLFormElement | null
  }) => {
    removeAllErrors(form ?? undefined)
    let isValid = true

    if (!validateField(fieldIds.internalLabel, data.internal_label, validateInput)) {
      isValid = false
    }

    if (!validateField(fieldIds.publicLabel, data.public_label, validateInput)) {
      isValid = false
    }

    return isValid
  }

  const submit = async (form: OrderTypeForm) => {
    if (!organizationId) return

    const internal_label = form.internal_label.trim()
    const public_label = form.public_label.trim()
    const payload: OrderTypeCreatePayload | OrderTypeUpdatePayload = orderType
      ? {
          internal_label,
          public_label,
          description: form.description.trim() || null,
          typical_duration_min: parseOptionalNumber(form.typical_duration_min),
          is_standard_type: Boolean(form.is_standard_type),
        }
      : {
          organization_id: organizationId,
          internal_label,
          public_label,
          description: form.description.trim() || null,
          typical_duration_min: parseOptionalNumber(form.typical_duration_min),
          is_standard_type: Boolean(form.is_standard_type),
        }
    try {
      const result = orderType
        ? await updateOrderType(orderType.id, payload)
        : await createOrderType(payload as OrderTypeCreatePayload)
      newNotification({
        class: "success",
        html: orderType ? "Auftragstyp aktualisiert." : "Auftragstyp angelegt.",
      })

      if (onSaved) {
        await onSaved(result)
      }

      onremoveOverlay?.()
    } catch (err) {
      console.error("order_type_save_failed", err)
      newNotification({ class: "error", html: "Auftragstyp konnte nicht gespeichert werden." })
    }
  }
</script>

<div class="overlay-form">
  <FormWrapper
    theme="light"
    mode={orderType ? "edit" : "create"}
    getInitObject={() => resetForm()}
    validateData={validateOrderType}
    onSubmit={submit}
    submitLabel={orderType ? "Aktualisieren" : "Anlegen"}
    showReset={false}
    reinitKey={orderType?.id ?? "new"}
    formSnippet={formContent}
    bind:submitting
    setSubmitFn={(fn) => (submitForm = fn)}
  />
</div>

{#snippet formContent(ctx: FormContext<OrderTypeForm>)}
  {@const { data, submitting } = ctx}
  <section class="row">
    <Input
      id={fieldIds.internalLabel}
      placeholder="Bezeichnung intern"
      bind:value={data.internal_label}
      disabled={submitting}
    />
    <Input
      id={fieldIds.publicLabel}
      placeholder="Bezeichnung öffentlich"
      bind:value={data.public_label}
      disabled={submitting}
    />
  </section>
  <section class="row">
    <Input
      id={fieldIds.typicalDuration}
      type="number"
      placeholder="Typische Dauer (Minuten)"
      bind:value={data.typical_duration_min}
      min={SCHEDULING_MINUTE_STEP}
      step={SCHEDULING_MINUTE_STEP}
      disabled={submitting}
    />
  </section>
  <section class="row">
    <Input
      id={fieldIds.description}
      type="textarea"
      placeholder="Aufforderungsbeschreibung"
      bind:value={data.description}
      disabled={submitting}
      brightBackground={true}
    />
  </section>
  <section class="row">
    <label class="standard-type" for={fieldIds.standardType}>
      <input
        id={fieldIds.standardType}
        type="checkbox"
        bind:checked={data.is_standard_type}
        disabled={submitting}
      />
      <span>
        <strong>Standardtyp für generischen Klebezettel</strong>
        <small>Wird vorgeschlagen, wenn kein konkreter Auftragstyp-Kontext vorhanden ist.</small>
      </span>
    </label>
  </section>
{/snippet}

{#snippet actionButtons()}
  <CTA
    type="secondaryButton"
    content="Abbrechen"
    onClick={() => close(submitting)}
    disabled={submitting}
  >
    {#snippet iconSnippet()}
      <Icon path={mdiClose} />
    {/snippet}
  </CTA>
  <CTA
    type="primaryButton"
    content={submitting ? "Speichere…" : orderType ? "Aktualisieren" : "Anlegen"}
    onClick={() => submitForm?.()}
    disabled={submitting}
  >
    {#snippet iconSnippet()}
      <Icon path={mdiContentSave} />
    {/snippet}
  </CTA>
{/snippet}

<style lang="less">
  @import "@kontextwerk/web-sdk/theme/tokens.less";

  .overlay-form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding-bottom: 1rem;
    height: fit-content;
    justify-content: flex-start;
  }

  :global(.overlay-form .formActions) {
    display: none;
  }

  .standard-type {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    align-items: start;
    gap: 0.75rem;
    width: 100%;
    padding: 0.75rem 0;
    border-top: 1px solid var(--line-weak);
    color: var(--text-invers-100);
    cursor: pointer;

    input {
      width: 1rem;
      height: 1rem;
      margin-top: 0.18rem;
      border-radius: 0;
    }

    span {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      min-width: 0;
    }

    strong {
      color: var(--text-invers-100);
      font-size: 0.92rem;
    }

    small {
      color: var(--text-invers-60);
      line-height: 1.35;
    }
  }
</style>
